import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import api from "../lib/api";
import { SOURCE_LABELS, TYPE_LABELS, newKey, productErrorMessage } from "../lib/product";
import { SelectField } from "../components/tickets/TicketDialogs";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const EMPTY = { title: "", description: "", type: "", source: "", estimate: "" };

// Nouveau retour (D-13): a page, optionally started from a ticket (?ticket=). Only the ticket's NUMBER is carried over — never its text
// or the requester's details: the person describes the need in their own words.
export default function ProductItemFormPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromTicket = params.get("ticket");
  const [ticket, setTicket] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [key, setKey] = useState(newKey);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [thanks, setThanks] = useState(null);

  useEffect(() => {
    if (!fromTicket) return;
    api.get(`/tickets/${fromTicket}`).then(({ data }) => {
      setTicket({ id: data.id, number: data.number, title: data.title });
      setDraft((d) => ({ ...d, source: d.source || "support", type: d.type || (data.category === "bug" ? "bug" : "improvement") }));
    }).catch(() => toast.error("Ticket introuvable : le retour ne lui sera pas rattaché."));
  }, [fromTicket]);

  // A new content is a new request: the Idempotency-Key only protects against the SAME tap sent twice.
  const change = (patch) => { setDraft((d) => ({ ...d, ...patch })); setKey(newKey()); setError(""); };
  const set = (name) => (value) => change({ [name]: value?.target ? value.target.value : value });
  const estimate = draft.estimate === "" ? null : Number(draft.estimate);
  const valid = useMemo(() => draft.title.trim().length >= 3 && !!draft.type && !!draft.source && (estimate === null || (Number.isInteger(estimate) && estimate >= 0)),
    [draft, estimate]);

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      const body = {
        title: draft.title.trim(), description: draft.description.trim(), type: draft.type, source: draft.source,
        ...(ticket ? { ticket_id: ticket.id } : {}), ...(estimate !== null ? { pharmacies_estimate: estimate } : {}),
      };
      const { data } = await api.post("/product/items", body, { headers: { "Idempotency-Key": key } });
      toast.success(`Retour ${data.number} enregistré`);
      // A submit-only account is told the number and cannot read the item back (D-15): it gets a thank-you, not the item page.
      if (data.title) navigate(`/produit/${data.id}`);
      else setThanks(data);
    } catch (e) {
      setError(productErrorMessage(e, "Impossible d'enregistrer le retour. Réessayez."));
    } finally {
      setSaving(false);
    }
  };

  if (thanks) {
    return (
      <div className="space-y-4" data-testid="product-thanks">
        <Card className="p-8 text-center space-y-3">
          <CheckCircle2 className="mx-auto text-emerald-500" size={28} />
          <h1 className="font-display font-bold text-xl text-slate-900">Merci, c'est transmis</h1>
          <p className="text-sm text-slate-600 max-w-md mx-auto">Votre retour porte le numéro <span className="font-medium tabular-nums" data-testid="product-thanks-number">{thanks.number}</span>.
            L'équipe produit le qualifiera.</p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => { setThanks(null); setDraft(EMPTY); setTicket(null); setKey(newKey()); }}>Signaler un autre retour</Button>
            <Button variant="ghost" onClick={() => navigate(-1)}>Retour</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="product-form-page">
      <Link to="/produit" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Retours et feuille de route</Link>
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Nouveau retour</h1>
        <p className="text-sm text-slate-500">Un bug, une amélioration ou une demande à transmettre à l'équipe produit.</p>
      </div>

      <form className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <div className="space-y-4 min-w-0">
          {ticket && (
            <Card className="p-4 text-sm text-slate-700 bg-slate-50" data-testid="product-from-ticket">
              Signalé depuis le ticket <span className="font-medium tabular-nums">{ticket.number}</span>. Le texte du ticket n'est pas recopié : décrivez le besoin avec vos mots.
            </Card>
          )}
          <Card className="p-5 space-y-4">
            <h2 className="text-sm font-semibold text-slate-900">Le retour</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField id="product-type" label="Type *" value={draft.type} onChange={set("type")} options={TYPE_LABELS} />
              <SelectField id="product-source" label="D'où vient-il ? *" value={draft.source} onChange={set("source")} options={SOURCE_LABELS} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-title">Titre *</Label>
              <Input id="product-title" value={draft.title} onChange={set("title")} maxLength={140} placeholder="Le bouton Valider ne répond pas après un retour" data-testid="product-title" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="product-description">Description</Label>
              <Textarea id="product-description" value={draft.description} onChange={set("description")} rows={6} maxLength={4000} data-testid="product-description"
                placeholder="Ce qui se passe, dans quelle situation, ce que vous attendiez" />
              <p className="flex items-start gap-1.5 text-xs text-slate-500"><ShieldAlert size={13} className="shrink-0 mt-0.5" />Aucune donnée patient ni ordonnance.</p>
            </div>
            {!ticket && (
              <div className="space-y-1.5">
                <Label htmlFor="product-estimate">Pharmacies concernées (estimation)</Label>
                <Input id="product-estimate" type="number" min="0" inputMode="numeric" value={draft.estimate} onChange={set("estimate")} className="max-w-[10rem]" data-testid="product-estimate" />
                <p className="text-xs text-slate-500">Facultatif : utile quand le retour ne vient d'aucun ticket.</p>
              </div>
            )}
          </Card>

          {error && <p className="text-sm text-red-600" role="alert" data-testid="product-form-error">{error}</p>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
            <Button type="submit" disabled={!valid || saving} data-testid="product-form-submit">
              {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer le retour
            </Button>
          </div>
        </div>

        <aside className="hidden lg:block space-y-3">
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h2 className="font-semibold text-slate-900">Et ensuite ?</h2>
            <p>L'équipe produit lit chaque retour, le qualifie (gravité, effort, priorité) puis décide de le retenir, de le regrouper avec d'autres ou de l'écarter.</p>
          </Card>
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h2 className="font-semibold text-slate-900">Bug ou question ?</h2>
            <p>Un problème précis chez une pharmacie reste un <span className="font-medium">ticket</span> du support. Signalez au produit seulement ce qui demande un changement du logiciel.</p>
          </Card>
        </aside>
      </form>
    </div>
  );
}
